/**
 * Content for the landing page: an illustrative 6:32 menemen recipe video read into 11 scenes.
 * Shapes are [left %, top %, width %, height % (0 = circle), radius, colour, detection label].
 */
export type Mood = 'pos' | 'neu' | 'neg';
export type Shape = [number, number, number, number, string | number, string, string];

export interface Scene {
  start: number;
  end: number;
  title: string;
  mood: Mood;
  brands: string[];
  tags: string[];
  desc: string;
  flag?: string;
  shapes: Shape[];
}

export const TOTAL = 392;

const HOST: Shape[] = [
  [6, 10, 18, 34, '6px', '#2C2723', ''],
  [30, 15, 8, 0, 0, '#B88E6E', 'person'],
  [25.5, 32, 17, 54, '40px 40px 8px 8px', '#5C4033', ''],
];

export const SCENES: Scene[] = [
  {
    start: 0,
    end: 21,
    title: 'Welcome',
    mood: 'neu',
    brands: [],
    tags: ['kitchen', 'intro'],
    desc: "The host welcomes viewers in a sunlit home kitchen and introduces today's dish.",
    shapes: HOST,
  },
  {
    start: 21,
    end: 58,
    title: 'Ingredients',
    mood: 'neu',
    brands: ['Danone'],
    tags: ['ingredients', 'kitchen', 'recipe'],
    desc: 'Ingredients on the counter: tomatoes, green peppers, eggs and a pot of Danone yogurt.',
    shapes: [
      [8, 58, 7, 0, 0, '#A9473A', 'tomatoes'],
      [15.5, 61, 6, 0, 0, '#9B3F33', ''],
      [23, 63, 11, 8, '20px', '#55773A', 'peppers'],
      [36, 62, 4.5, 0, 0, '#E3D6BF', ''],
      [41, 63, 4.5, 0, 0, '#DCCDB3', ''],
      [47, 58, 7, 11, '4px', '#EEF2F8', 'Danone'],
    ],
  },
  {
    start: 58,
    end: 94,
    title: 'Dicing peppers',
    mood: 'pos',
    brands: [],
    tags: ['recipe', 'kitchen'],
    desc: 'Peppers are diced. The host settles the onion question with a laugh: none today.',
    shapes: [
      [8, 60, 36, 14, '8px', '#7E6044', 'cutting board'],
      [16, 63.5, 3, 0, 0, '#55773A', ''],
      [22, 64.5, 3, 0, 0, '#4E6E35', ''],
      [28, 63, 3, 0, 0, '#55773A', ''],
      [34, 55, 16, 3, '3px', '#C9CCD1', ''],
    ],
  },
  {
    start: 94,
    end: 132,
    title: 'Butter in the pan',
    mood: 'neu',
    brands: ['Bosch'],
    tags: ['recipe', 'kitchen'],
    desc: 'Butter melts in a copper sahan on a Bosch cooktop.',
    shapes: [
      [6, 64, 46, 22, '8px', '#1A1A1C', 'Bosch'],
      [16, 40, 20, 0, 0, '#7A4A2A', 'pan'],
      [23.5, 53, 5, 0, 0, '#E9D27A', ''],
    ],
  },
  {
    start: 132,
    end: 169,
    title: 'Kids ask for breakfast',
    mood: 'pos',
    brands: [],
    tags: ['family', 'breakfast'],
    desc: 'Two kids run in asking when breakfast will be ready.',
    shapes: [
      [12, 30, 6, 0, 0, '#C49A78', 'child'],
      [10.4, 42, 9.2, 46, '30px 30px 6px 6px', '#3E5A7A', ''],
      [27, 34, 5.5, 0, 0, '#B58B6A', 'child'],
      [25.25, 45, 9, 43, '30px 30px 6px 6px', '#7A3E4E', ''],
      [42, 18, 8, 0, 0, '#B88E6E', ''],
    ],
  },
  {
    start: 169,
    end: 201,
    title: 'Tomatoes simmer',
    mood: 'neu',
    brands: [],
    tags: ['recipe', 'kitchen'],
    desc: 'Grated tomatoes go into the pan and slowly cook down.',
    shapes: [
      [12, 40, 24, 0, 0, '#7A4A2A', 'pan'],
      [15.5, 46, 17, 0, 0, '#A8402F', ''],
      [42, 36, 7, 30, '6px', '#9AA0A6', 'grater'],
    ],
  },
  {
    start: 201,
    end: 238,
    title: 'Accident on the TV news',
    mood: 'neg',
    brands: [],
    tags: ['news', 'accident'],
    flag: 'Death, injury or military conflict',
    desc: 'A TV in the background shows news footage of a traffic accident.',
    shapes: [
      [7, 12, 32, 38, '8px', '#0E0E10', 'TV, news footage'],
      [8.6, 15, 28.8, 32, '4px', '#4A2E2A', ''],
      [16, 33, 9, 7, '4px', '#C7C2B8', ''],
      [26, 35, 6, 5, '3px', '#8E2B22', ''],
      [40, 56, 14, 0, 0, '#7A4A2A', ''],
    ],
  },
  {
    start: 238,
    end: 271,
    title: 'Eggs folded in',
    mood: 'neu',
    brands: [],
    tags: ['recipe', 'breakfast'],
    desc: 'Eggs are cracked in and gently folded, still soft.',
    shapes: [
      [12, 40, 24, 0, 0, '#7A4A2A', ''],
      [15.5, 46, 17, 0, 0, '#B04A33', ''],
      [19, 54, 4, 0, 0, '#E8B33A', 'eggs'],
      [25.5, 58, 4, 0, 0, '#E2A92F', ''],
      [40, 48, 16, 3, '3px', '#A88B6A', ''],
    ],
  },
  {
    start: 271,
    end: 310,
    title: 'The table is set',
    mood: 'pos',
    brands: ['Lipton'],
    tags: ['breakfast', 'family'],
    desc: 'The family sets the table: olives, white cheese and Lipton tea in tulip glasses.',
    shapes: [
      [4, 62, 54, 30, '8px', '#4A3528', ''],
      [10, 47, 4, 15, '2px 2px 8px 8px', '#8E3B1F', 'Lipton'],
      [16, 48, 4, 14, '2px 2px 8px 8px', '#8E3B1F', ''],
      [24, 56, 11, 0, 0, '#E8E2D6', ''],
      [27.5, 62, 4, 0, 0, '#2F2A22', ''],
      [40, 60, 10, 7, '4px', '#F1EDE2', 'cheese'],
    ],
  },
  {
    start: 310,
    end: 357,
    title: 'Final plating',
    mood: 'pos',
    brands: ['Oreo'],
    tags: ['breakfast', 'recipe', 'kitchen', 'family'],
    desc: 'Final plating of the menemen. The host tastes it and recommends serving it with fresh bread.',
    shapes: [
      [12, 40, 26, 0, 0, '#6E4026', 'menemen'],
      [15.5, 46.2, 19, 0, 0, '#C2563A', ''],
      [19, 53, 5, 0, 0, '#E6B848', ''],
      [26.5, 58, 4, 0, 0, '#E0AE3E', ''],
      [41, 54, 13, 15, '40px 40px 8px 8px', '#C99A5B', 'bread'],
      [39, 16, 10, 20, '4px', '#1F3F8F', 'Oreo'],
    ],
  },
  {
    start: 357,
    end: 392,
    title: 'Sign-off',
    mood: 'neu',
    brands: [],
    tags: ['outro'],
    desc: "The host thanks viewers and asks them to subscribe for next week's recipe.",
    shapes: HOST,
  },
];

export const MOOD: Record<Mood, string> = { pos: 'Positive', neu: 'Neutral', neg: 'Negative' };

/** [tag, label, is exclusion] */
export const CHIPS: Array<[string, string, boolean]> = [
  ['breakfast', 'breakfast', false],
  ['family', 'family', false],
  ['kitchen', 'kitchen', false],
  ['football', 'football', false],
  ['accident', 'never: accident', true],
  ['alcohol', 'never: alcohol', true],
];
export const DEFAULT_CHIPS: Record<string, boolean> = { breakfast: true, family: true, accident: true };

export const FAQ: Array<{ q: string; a: string }> = [
  {
    q: 'How is this different from keyword targeting?',
    a: 'Keywords describe the title and tags a creator picked. Scenewise describes what actually happens in the video, scene by scene: the brands, the mood, the topic and whether a moment is safe. A cooking video with a news clip playing in the background looks clean to keywords. Scenewise flags the 37 seconds that are not.',
  },
  {
    q: 'Which languages does it understand?',
    a: 'Any language. It reads speech, on-screen text and packaging, so a brand counts whether it is printed on a pack, shown on screen or said out loud. The example video on this page is in Turkish to show exactly that.',
  },
  {
    q: 'Where does the targeting go?',
    a: 'The scenes that qualify become placement lists and bidding signals for your programmatic platform, or CSV and JSON for your own tools. On partner sites the same segment can run as an overlay that appears at the exact second the moment starts.',
  },
  {
    q: 'How long does analysis take?',
    a: 'About fifteen seconds for a six-minute video. A library of thousands of videos is read in a day, so the targeting is ready before the first impression is served.',
  },
  {
    q: 'Who is behind Scenewise?',
    a: 'Scenewise is a product of Skysection, a media technology company in Istanbul. The pilot team works with you directly, from the brief to the first report.',
  },
];

export const fmt = (t: number) => {
  const s = Math.max(0, Math.floor(t));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
export const sceneAt = (t: number) => {
  const i = SCENES.findIndex((s) => t < s.end);
  return i < 0 ? SCENES.length - 1 : i;
};
export const joinOr = (list: string[]) =>
  list.length < 2 ? list.join('') : `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}`;
